<template>
  <div class="flyer-view flex h-[calc(100vh-80px)]">
    <!-- Détecteur de changement de marque -->
    <BrandChangeDetector :support-id="route.params.id" />

    <!-- Overlay de chargement -->
    <div v-if="isSaving" class="pdf-loading-overlay">
      <div class="pdf-loading-content loader-standard">
        <div class="spinner" />
        <div class="loading-text">
          Enregistrement en cours…
        </div>
        <div class="loading-timer">{{ saveElapsedLabel }}</div>
        <div class="loading-subtext">
          Merci de patienter jusqu'à la fin de l'enregistrement.
        </div>
      </div>
    </div>

    <!-- Modal de sélection des pages pour la sauvegarde -->
    <div v-if="showPageSelectionModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">
            Sélection des pages
          </h3>
          <button class="modal-close" @click="closePageSelectionModal">
            <i class="fas fa-times" />
          </button>
        </div>
        <div class="modal-body">
          <p class="modal-description">
            Choisissez quelles pages vous souhaitez inclure dans votre PDF lors
            de la sauvegarde :
          </p>
          <div class="page-options">
            <label class="page-option">
              <input
                v-model="selectedPages"
                type="radio"
                value="both"
                class="page-radio"
              >
              <span class="page-option-text">
                <i class="fas fa-file-alt" />
                Recto et verso
              </span>
            </label>
            <label class="page-option">
              <input
                v-model="selectedPages"
                type="radio"
                value="recto"
                class="page-radio"
              >
              <span class="page-option-text">
                <i class="fas fa-file-alt" />
                Recto uniquement
              </span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closePageSelectionModal">
            Annuler
          </button>
          <button class="btn-primary" @click="confirmSaveSupport">
            Enregistrer
          </button>
        </div>
      </div>
    </div>

    <!-- Panneau latéral -->
    <ProductsPanel />

    <!-- Zone principale -->
    <div class="flex-1 bg-gray-100 p-8 overflow-auto">
      <div class="max-w-5xl mx-auto">
        <!-- En-tête -->
        <div class="mb-8 flex items-center justify-between">
          <h1 class="text-2xl font-bold text-gray-900">
            {{ route.params.id ? "Édition d'un flyer" : "Création d'un flyer" }}
          </h1>
          <div class="flex items-center gap-4">
            <button
              v-if="currentSide === 'recto'"
              class="px-4 py-2 bg-brand text-white rounded-md shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              :disabled="isSaving"
              @click="handleSaveSupport()"
            >
              <i v-if="isSaving" class="fas fa-spinner fa-spin" />
              <span>{{ isSaving ? "Enregistrement..." : "Enregistrer" }}</span>
            </button>
            <div v-else class="text-sm text-gray-500 italic">
              Retournez au recto pour enregistrer ou générer le PDF
            </div>
          </div>
        </div>

        <!-- Configuration -->
        <div v-if="!supportStore.isPreview" class="mb-8 grid grid-cols-2 gap-4">
          <div class="flex items-end gap-2">
            <button
              class="px-4 py-2 rounded-md shadow-sm transition-colors"
              :class="
                currentSide === 'recto'
                  ? 'bg-brand text-white'
                  : 'bg-white border border-gray-300 hover:bg-gray-50'
              "
              @click="toggleSide('recto')"
            >
              Recto
            </button>
            <button
              class="px-4 py-2 rounded-md shadow-sm transition-colors"
              :class="
                currentSide === 'verso'
                  ? 'bg-brand text-white'
                  : 'bg-white border border-gray-300 hover:bg-gray-50'
              "
              @click="toggleSide('verso')"
            >
              {{
                supportStore.currentSupport?.nb_pages > 1
                  ? "Verso"
                  : "Ajouter Verso"
              }}
            </button>
          </div>
        </div>

        <!-- Canvas -->
        <SupportCanvas
          v-if="supportStore.currentSupport"
          ref="supportCanvasRef"
          :support="supportStore.currentSupport"
          :show-header="currentSide === 'recto'"
          :selected-pages="selectedPages"
          :pages-to-generate="selectedPages"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ProductsPanel from "@/components/supports/panels/ProductsPanel.vue";
import SupportCanvas from "@/components/supports/SupportCanvas.vue";
import BrandChangeDetector from "@/components/supports/BrandChangeDetector.vue";
import { useSupportStore } from "@/stores/supports/useSupportStore";
import { useHtmlDownload } from "@/composables/supports/useHtmlDownload";
import { computed, onMounted, ref, watch } from "@vue/runtime-core";
import type { Support, SupportPage, SupportElement } from "@/types/support";
import { useToast } from "vue-toastification";
import { useRoute, useRouter } from "vue-router";

const supportStore = useSupportStore();
const format = ref("A4");
const currentSide = ref<"recto" | "verso">("recto");
const supportTemplateId = ref(2);
const hasVerso = ref(false);
const isSaving = ref(false);

// Chrono d'enregistrement : l'enregistrement d'un flyer est long (mise à jour du
// support, génération HTML recto/verso, envoi). On affiche un compteur qui tourne
// tant que isSaving est vrai, pour rassurer l'utilisateur.
const saveElapsed = ref(0);
let saveTimer: ReturnType<typeof setInterval> | null = null;
const saveElapsedLabel = computed(() => {
  const m = Math.floor(saveElapsed.value / 60);
  const s = saveElapsed.value % 60;
  return m > 0 ? `${m} min ${String(s).padStart(2, "0")} s` : `${s} s`;
});
const startSaveTimer = () => {
  saveElapsed.value = 0;
  if (saveTimer) clearInterval(saveTimer);
  saveTimer = setInterval(() => {
    saveElapsed.value++;
  }, 1000);
};
const stopSaveTimer = () => {
  if (saveTimer) {
    clearInterval(saveTimer);
    saveTimer = null;
  }
};

// Variables pour la modal de sélection des pages
const showPageSelectionModal = ref(false);
const selectedPages = ref<string>("both");

// Props supprimées car nous utilisons directement le store

const toast = useToast();
const route = useRoute();
const router = useRouter();

const supportCanvasRef = ref();
const { checkForHtmlDownload } = useHtmlDownload(supportCanvasRef);

const successSound = new Audio("/sounds/success.wav");
const errorSound = new Audio("/sounds/error.mp3");

const toggleSide = (side: "recto" | "verso") => {
  if (side === "verso" && !hasVerso.value && supportStore.currentSupport) {
    hasVerso.value = true;
    // Récupérer la page recto existante
    const rectoPage = supportStore.currentSupport.pages.find(
      (page: SupportPage) => page.page_number === 1
    );

    if (rectoPage) {
      // Créer une nouvelle page verso vide
      const newPage = {
        id: supportStore.currentSupport.pages.length || 0,
        page_number: 2,
        support_id: supportStore.currentSupport.id || 0,
        nb_max_products: 5,
        page_settings: {
          margin_top: 10,
          margin_bottom: 10,
          margin_left: 10,
          margin_right: 10,
          bleed: 5,
          safe_area: true,
        },
        background_image: null,
        page_zoom: "1.00",
        page_rotation: 0,
        page_position: {
          x: 0,
          y: 0,
          width: 595,
          height: 842,
        },
        elements: [], // Page verso vide
      };

      supportStore.updateSupport({
        support_template_id: supportTemplateId.value,
        pages: [{ ...rectoPage, elements: rectoPage.elements || [] }, newPage],
        nb_pages: 2,
      });
    }
  }
  currentSide.value = side;
};

// Fonction factorisée pour générer le PDF à partir du composant affiché
const generateAndGetPdfBlob = async (): Promise<Blob> => {
  // Vérifier que le support et le canvas existent
  if (!supportStore.currentSupport || !supportCanvasRef.value) {
    throw new Error("Support ou canvas non disponible");
  }

  let previewWasActivated = false;
  try {
    if (!supportStore.isPreview) {
      supportStore.togglePreview();
      previewWasActivated = true;
      // Attendre que le DOM soit mis à jour en mode aperçu
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    // Vérifier que le canvas a bien la méthode generatePdfBlob
    if (typeof supportCanvasRef.value.generatePdfBlob !== 'function') {
      throw new Error("La méthode generatePdfBlob n'est pas disponible sur le canvas");
    }

    // Générer le PDF avec un timeout et les pages sélectionnées
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error("Timeout: La génération du PDF a pris trop de temps")), 30000)
    );
    
    const pdfPromise = supportCanvasRef.value.generatePdfBlob(selectedPages.value);
    const pdfBlob = await Promise.race([pdfPromise, timeoutPromise]);
    
    if (!pdfBlob) {
      throw new Error("Le PDF généré est vide");
    }
    
    return pdfBlob as Blob;
  } catch (error) {
    console.error("Erreur lors de la génération du PDF:", error);
    throw error;
  } finally {
    if (previewWasActivated) {
      try {
        supportStore.togglePreview();
      } catch (toggleError) {
        console.warn("Erreur lors du retour au mode édition:", toggleError);
      }
    }
  }
};

const playSound = (sound: HTMLAudioElement) => {
  sound.currentTime = 0; // Réinitialiser le son
  sound.play().catch((error) => {
    console.warn("Erreur lors de la lecture du son:", error);
  });

  // Couper le son après 200ms pour le son iPhone
  setTimeout(() => {
    sound.pause();
    sound.currentTime = 0;
  }, 400);
};

// Ajuster le volume des sons
successSound.volume = 0.3; // 30% du volume pour le son iPhone
errorSound.volume = 0.15; // 15% du volume pour l'erreur

// Fonctions de gestion de la modal
const closePageSelectionModal = () => {
  showPageSelectionModal.value = false;
  selectedPages.value = "both"; // Reset à la valeur par défaut
};

const handleSaveSupport = () => {
  // Afficher la modal de sélection des pages pour les flyers
  showPageSelectionModal.value = true;
};

const confirmSaveSupport = async () => {
  showPageSelectionModal.value = false;
  await saveSupport();
};

const saveSupport = async () => {
  try {
    isSaving.value = true;
    startSaveTimer();

    // Vérifier que le support existe
    if (!supportStore.currentSupport) {
      throw new Error("Aucun support à sauvegarder");
    }

    // Vérifier que le canvas existe et est prêt
    if (!supportCanvasRef.value) {
      throw new Error("Le canvas n'est pas encore initialisé");
    }

    // 1. Mettre à jour le support selon la sélection des pages
    if (selectedPages.value === "recto" && supportStore.currentSupport.pages.length > 1) {
      // Si on ne veut que le recto, on garde seulement la première page
      const rectoPage = supportStore.currentSupport.pages[0];
      supportStore.updateSupport({
        pages: [rectoPage],
        nb_pages: 1,
        printType: "recto"
      });
    } else if (selectedPages.value === "both" && supportStore.currentSupport.pages.length === 1) {
      // Si on veut les deux pages mais qu'on n'a que le recto, on ajoute le verso vide
      const rectoPage = supportStore.currentSupport.pages[0];
      const newVersoPage = {
        id: supportStore.currentSupport.pages.length || 0,
        page_number: 2,
        support_id: supportStore.currentSupport.id || 0,
        nb_max_products: 5,
        page_settings: {
          margin_top: 10,
          margin_bottom: 10,
          margin_left: 10,
          margin_right: 10,
          bleed: 5,
          safe_area: true,
        },
        background_image: null,
        page_zoom: "1.00",
        page_rotation: 0,
        page_position: {
          x: 0,
          y: 0,
          width: 595,
          height: 842,
        },
        elements: [],
      };
      supportStore.updateSupport({
        pages: [rectoPage, newVersoPage],
        nb_pages: 2,
        printType: "recto-verso"
      });
    }

    // 2-3. Sauvegarder le support d'abord. Le PDF est généré côté serveur
    // (vectoriel, asynchrone) ; la capture client ne sert qu'en fallback.
    // S'assurer que les données name et dates sont bien synchronisées depuis le SupportCanvas
    // Ces valeurs sont normalement mises à jour par les fonctions updateTitle et updateValidityPeriod
    // mais on force la synchronisation ici pour être sûr
    if (supportCanvasRef.value && supportStore.currentSupport) {
      const canvas = supportCanvasRef.value;
      if (canvas.titleText?.value) {
        supportStore.currentSupport.name = canvas.titleText.value;
      }
      if (canvas.validityPeriod?.value) {
        supportStore.currentSupport.dates = canvas.validityPeriod.value;
      }
    }

    // Laisser le store gérer la préparation et le nettoyage des données
    let response;
    try {
      response = await supportStore.saveSupport();
      if (response?.data?.id) {
        supportStore.currentSupport.id = response.data.id;
      }
    } catch (saveError) {
      console.error("Erreur lors de la sauvegarde du support:", saveError);
      toast.error("Erreur lors de la sauvegarde des données du support");
      return;
    }

    // 4. FormData (sans PDF : le serveur génère le vectoriel ; capture en fallback)
    const formData = new FormData();
    formData.append("supportId", supportStore.currentSupport.id.toString());
    formData.append("brandSlug", localStorage.getItem("brand") || "hv");
    formData.append(
      "supportType",
      supportStore.currentSupport.type || supportStore.supportType
    );
    formData.append("selectedPages", selectedPages.value);

    // 5. Persistance du PDF (vectoriel async + notification ; capture en fallback)
    try {
      await supportStore.persistSupportPdf(
        formData,
        () => generateAndGetPdfBlob(),
        // HTML EXACT de l'éditeur -> rendu vectoriel fidèle au pixel côté serveur.
        () => supportCanvasRef.value.generateFullHTML()
      );
      playSound(successSound);
      toast.success("Support sauvegardé avec succès");
      
      // Attendre un peu avant de rediriger pour permettre à l'utilisateur de voir le message
      setTimeout(() => {
        router.push("/documents");
      }, 1000);
    } catch (pdfSendError) {
      console.error("Erreur lors de l'envoi du PDF:", pdfSendError);
      toast.error("Le support a été sauvegardé mais l'envoi du PDF a échoué");
      // On ne redirige pas en cas d'erreur
    }
  } catch (error) {
    console.error("Erreur générale lors de la sauvegarde:", error);
    playSound(errorSound);
    const errorMessage = error instanceof Error ? error.message : "Erreur inconnue";
    toast.error(`Erreur lors de la sauvegarde: ${errorMessage}`);
    // Ne pas re-throw l'erreur pour éviter les exceptions non gérées
  } finally {
    isSaving.value = false;
    stopSaveTimer();
  }
};

onMounted(async () => {
  const supportId = route.params.id;
  if (supportId) {
    // Mode édition
    try {
      await supportStore.loadSupport("flyer", supportId as string);
      // Mettre à jour les refs avec les données du support
      if (supportStore.currentSupport) {
        hasVerso.value = supportStore.currentSupport.nb_pages > 1;
        // S'assurer que les deux pages sont correctement initialisées
        if (supportStore.currentSupport.pages.length === 2) {
          const [page1, page2] = supportStore.currentSupport.pages;

          // Mettre à jour le store avec les deux pages
          supportStore.updateSupport({
            support_template_id: supportTemplateId.value,
            pages: [
              {
                ...page1,
                elements: page1.elements || [],
              },
              {
                ...page2,
                elements: page2.elements || [],
              },
            ],
            nb_pages: 2,
          });
        }
      }
    } catch (error) {
      console.error("Erreur lors du chargement du support:", error);
      toast.error("Erreur lors du chargement du support");
    }
  } else {
    // Mode création
    supportStore.initSupport("flyer");
  }
  
  // Vérifier si on doit télécharger le HTML automatiquement
  checkForHtmlDownload();
});

watch([format, currentSide, hasVerso], () => {
  if (supportStore.currentSupport) {
    supportStore.updateSupport({
      format: format.value,
      printType: hasVerso.value ? "recto-verso" : "recto",
      currentSide: currentSide.value,
      support_template_id: supportTemplateId.value,
      pages: supportStore.currentSupport.pages.map((page: any) => ({
        ...page,
        elements:
          page.elements?.map((element: any) => ({
            ...element,
            product_image_id: element?.product_image_id,
          })) || [],
      })),
    });
  }
});
</script>

<script lang="ts">
export default {
  name: "FlyerView",
};
</script>

<style scoped>
/* Styles pour l'impression et l'aperçu */
@media print {
  .support-preview {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
  }
}

/* Styles pour l'overlay de chargement */
.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.pdf-loading-content {
  text-align: center;
}

.loader-standard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 56px;
  height: 56px;
  border: 6px solid #e5e7eb;
  border-top: 6px solid #b70018;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  color: #2d3a3a;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.loading-timer {
  margin-top: 0.5rem;
  font-size: 1.75rem;
  font-weight: 700;
  color: #b70018;
  font-variant-numeric: tabular-nums;
}

.loading-subtext {
  margin-top: 0.35rem;
  font-size: 0.9rem;
  color: #6b7280;
}

/* Styles pour la modal de sélection des pages */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.modal-close:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
}

.modal-description {
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.page-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.page-option {
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.page-option:hover {
  border-color: #b70018;
  background-color: #fef2f2;
}

.page-radio {
  margin-right: 0.75rem;
  width: 1.25rem;
  height: 1.25rem;
  accent-color: #b70018;
}

.page-option-text {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #374151;
}

.page-option-text i {
  margin-right: 0.5rem;
  color: #b70018;
  font-size: 1.125rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.btn-secondary {
  padding: 0.75rem 1.5rem;
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background-color: #e5e7eb;
  border-color: #9ca3af;
}

.btn-primary {
  padding: 0.75rem 1.5rem;
  background-color: #b70018;
  color: white;
  border: 1px solid #b70018;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background-color: #8c0014;
  border-color: #8c0014;
}
</style>
